import { useEffect, useMemo, useRef } from 'react';
import { ArrowDownIcon, ArrowLeftIcon, ArrowRightIcon, ArrowUpIcon } from '@radix-ui/react-icons';
import type { EbookQuestion } from '@wx-weiqi/contracts';
import { layoutAnswerTree } from '../services/ebook-variations';

export function EbookVariationTree({
  nodes,
  selected,
  disabled,
  onSelect,
}: {
  nodes: EbookQuestion['nodes'];
  selected: string | null;
  disabled: boolean;
  onSelect: (id: string | null) => void;
}) {
  const layout = useMemo(() => layoutAnswerTree(nodes), [nodes]);
  const viewport = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = viewport.current,
      item = layout.items.find((item) => item.id === selected);
    if (!element || !item) return;
    // Scroll only the tree, never the surrounding editor/page.
    if (item.x < element.scrollLeft || item.x + 152 > element.scrollLeft + element.clientWidth)
      element.scrollLeft = Math.max(0, item.x - 24);
    if (item.y - 28 < element.scrollTop || item.y + 28 > element.scrollTop + element.clientHeight)
      element.scrollTop = Math.max(0, item.y - element.clientHeight / 2);
  }, [layout, selected]);
  return (
    <div className="ebook-tree-workspace">
      <div className="ebook-tree-pan" aria-label="浏览变化树">
        <button
          aria-label="变化树向左"
          onClick={() => viewport.current?.scrollBy({ left: -192, behavior: 'smooth' })}
        >
          <ArrowLeftIcon />
        </button>
        <button
          aria-label="变化树向右"
          onClick={() => viewport.current?.scrollBy({ left: 192, behavior: 'smooth' })}
        >
          <ArrowRightIcon />
        </button>
        <button
          aria-label="变化树向上"
          onClick={() => viewport.current?.scrollBy({ top: -152, behavior: 'smooth' })}
        >
          <ArrowUpIcon />
        </button>
        <button
          aria-label="变化树向下"
          onClick={() => viewport.current?.scrollBy({ top: 152, behavior: 'smooth' })}
        >
          <ArrowDownIcon />
        </button>
        <button
          onClick={() => {
            const item = layout.items.find((node) => node.id === selected);
            if (item)
              viewport.current?.scrollTo({
                left: Math.max(0, item.x - 24),
                top: Math.max(0, item.y - 80),
                behavior: 'smooth',
              });
          }}
        >
          定位
        </button>
      </div>
      <div ref={viewport} className="ebook-tree" role="region" aria-label="答案变化树" tabIndex={0}>
        <div className="ebook-tree-canvas" style={{ width: layout.width, height: layout.height }}>
          <svg
            className="ebook-tree-links"
            width={layout.width}
            height={layout.height}
            aria-hidden="true"
          >
            {layout.edges.map(({ parent, child }) => (
              <path
                key={child.id}
                d={`M ${parent.x + 152} ${parent.y} H ${parent.x + 172} V ${child.y} H ${child.x}`}
              />
            ))}
          </svg>
          {layout.items.map((item) => (
            <button
              key={item.id ?? 'root'}
              disabled={disabled}
              aria-pressed={item.id === selected}
              data-node-id={item.id ?? 'root'}
              data-outcome={item.node?.outcome}
              style={{ left: item.x, top: item.y - 28 }}
              onClick={() => onSelect(item.id)}
            >
              {item.node ? (
                <>
                  <span>
                    {item.depth} 手 · {item.node.move.color === 'BLACK' ? '黑' : '白'}{' '}
                    {item.node.move.point
                      ? `${item.node.move.point.x + 1},${item.node.move.point.y + 1}`
                      : '虚着'}
                  </span>
                  {item.node.outcome !== 'CONTINUE' && (
                    <span className="ebook-tree-outcome">
                      {item.node.outcome === 'CORRECT' ? '✓ 正确答案' : '× 错误答案'}
                    </span>
                  )}
                </>
              ) : (
                '初始局面（根）'
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
